import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * LiveWaveform — overlay shown in the omnibar's input row while a
 * voice recording is in progress.
 *
 * Composition (left to right):
 *
 *   [REC] [0:08] [▁▃▅▆▇█▇▆▅▃▁ rolling bars]                [✕ cancel]
 *
 *   • `REC` dot — coral pulse that asserts the live-recording state at
 *     a glance even if the user has scrolled the bars off the visible
 *     area.
 *   • Duration — monospace tabular-numerals so the timer's frame
 *     doesn't jitter horizontally as digits flip.
 *   • Bars — driven directly off the `levels` prop. The component is
 *     intentionally dumb: it just maps each amplitude to a bar height
 *     and renders. The hook owning the recording (`useVoiceRecorder`)
 *     handles the rolling window, padding, and frame timing.
 *   • Cancel button — explicit way to back out of recording without
 *     keeping the audio. The mic button in the meta row already
 *     toggles to "stop" while recording (which DOES keep the audio);
 *     this cancel is the discard path.
 *
 * The component renders bars as plain `<div>`s rather than a `<canvas>`
 * because:
 *
 *   1. The bar count is small (`LIVE_BAR_COUNT` in the hook ≈ 48) so
 *      DOM-node overhead is well below the threshold where canvas wins.
 *   2. DOM bars cooperate naturally with CSS transitions / theming
 *      (recording state can recolour every bar via a single class
 *      change without touching the render loop).
 *   3. The bars need to coexist with focus / pointer events on the
 *      surrounding pill — a `<canvas>` would either intercept those
 *      or need `pointer-events: none` plumbing that's just more cruft.
 */
import React, { memo } from "react";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
/**
 * Minimum normalised bar height so a silent moment still reads as
 * "bars are here, the recorder is alive" rather than disappearing
 * entirely. Without this the live meter would visibly collapse on
 * each inter-syllable pause; with it the bars subtly breathe at the
 * floor amplitude and the user gets steady feedback that recording
 * is in progress.
 */
const MIN_BAR_HEIGHT = 0.06;
/**
 * Soft ceiling on the normalised input amplitude so a loud burst (a
 * cough into the mic, a chair scrape) doesn't pin every subsequent
 * bar at full height. Inputs above this are clipped before the
 * height calculation; the displayed bar still maxes out, but
 * subsequent quieter bars regain their relative proportions in the
 * next frame.
 */
const AMPLITUDE_CEILING = 0.85;
export const LiveWaveform = memo(({ levels, durationSeconds, onCancel, }) => {
    return (_jsxs("div", { className: "omnibar-draft__live-waveform", role: "status", "aria-live": "polite", children: [_jsx("span", { className: "omnibar-draft__live-waveform-rec", "aria-hidden": "true" }), _jsx("span", { className: "omnibar-draft__live-waveform-time", children: formatDuration(durationSeconds) }), _jsx("div", { className: "omnibar-draft__live-waveform-bars", "aria-hidden": "true", children: levels.map((level, i) => {
                    const clipped = Math.min(level, AMPLITUDE_CEILING) / AMPLITUDE_CEILING;
                    const height = MIN_BAR_HEIGHT + clipped * (1 - MIN_BAR_HEIGHT);
                    return (_jsx("span", { className: "omnibar-draft__live-waveform-bar", style: { height: `${(height * 100).toFixed(1)}%` } }, i));
                }) }), _jsx("button", { type: "button", className: "omnibar-draft__live-waveform-cancel", "aria-label": "Cancel recording", title: "Cancel recording", tabIndex: -1, 
                // Suppress focus shift so cancelling doesn't blur the editor /
                // textarea — the user can be drafting text while the voice
                // note records, and we don't want the cancel to disrupt that.
                onMouseDown: (e) => e.preventDefault(), onClick: onCancel, children: _jsx(FaIcon, { icon: faXmark }) })] }));
});
/**
 * Format a duration in seconds as `M:SS`. Strictly two digits in the
 * seconds field so the column width is stable across the leading-zero
 * boundary. Negative inputs (shouldn't happen, but…) are clamped to 0.
 */
function formatDuration(seconds) {
    const safe = Math.max(0, Math.floor(seconds));
    const m = Math.floor(safe / 60);
    const s = safe % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
}
